/* eslint-disable camelcase */
import { userApi } from './index';
import { config } from '../config';
import { CalculateData, OrderParams, PreparedCalculateData, UnionServicesExIdsKeys } from '../../../models/Calculate';
import {
  CommonShare,
  CtvPreset,
  InnerAccount,
  InternetPreset,
  IptvPreset,
  ServiceEx,
  ServicePreset,
  ShareNames,
  TransformServicePreset,
  UnionPreset,
} from '../../../models/ServicePresets';
import { AccountTypes } from '../../../models/Common';
import { IPaymentCard } from '../../../models/PaymentCard';
import { PaymentHistory, PaymentBonuses } from '../../../models/PaymentHistory';
import { share50PercentId } from '../../../constants/configuration';
import { checkIsShareAvailableForService } from '../../../utils';

type CalculateCostProps = OrderParams;
type CreateOrderProps = OrderParams;

interface CreateServiceOrderResponse {
  Code: string;
  Amount: number;
}

export interface GetLinkToPlatonFormProps {
  amount: number;
  description: string;
  url: string;
  error_url?: string;
  order?: string;
  source: string;
  cardId?: number;
  saveCard?: boolean | number;
  promoBannerId?: string;
}

interface GetLinkToPlatonFormResponse {
  short_link: string;
  upload_qr: string;
}

interface GetServicePresetsProps {
  accountId: string | number;
  accountType: AccountTypes | string;
}

interface TransformGetServicePresetsProps {
  accountId: string | number;
  accountType: AccountTypes | string;
  serviceForPaymentId: number;
}

export interface GetPaymentHistoryProps {
  type?: string;
  serviceType?: AccountTypes | '';
  period?: string;
  accountId?: number;
  page?: number;
  subAccountId?: number;
}

export interface GooglePayPlatonData {
  amount: number;
  description: string;
  url: string;
  order?: string;
  source: string;
  token: string;
  email: string;
}

export interface GooglePayPlatonResponse {
  action: string;
  order_id: string;
  redirect_method: string;
  redirect_params: GooglePayPlatonResponseRedirectParams | null;
  redirect_url: string;
  result: string;
  status: string;
  trans_date: string;
  trans_id: string;
}

interface GooglePayPlatonResponseRedirectParams {
  PaReq: string;
  MD: string;
  TermUrl: string;
}

interface OrdersInfoResponse {
  Amount: number;
  Card: IPaymentCard | null;
  CreateDate: string;
  CustomerID: number;
  OrderCode: string;
  ProcessDate: string;
  FailureReason: string;
  Status: number;
  id: number;
}

export interface ServicePresetsAllResponse {
  ctv: CtvPreset[];
  internet: InternetPreset[];
  iptv: IptvPreset[];
  union: UnionPreset[];
}

export const paymentsApi = userApi.injectEndpoints({
  endpoints: (build) => ({
    getServicePresets: build.query<ServicePreset, GetServicePresetsProps>({
      query: ({ accountId, accountType }) => ({
        url: config.PAYMENTS.GET_SERVICE_PRESETS(accountId, accountType),
      }),
      keepUnusedDataFor: 0,
      providesTags: () => ['Preset'],
    }),
    getServicePresetsWithTransformResponse: build.query<TransformServicePreset, TransformGetServicePresetsProps>({
      query: ({ accountId, accountType }) => ({
        url: config.PAYMENTS.GET_SERVICE_PRESETS(accountId, accountType),
      }),
      transformResponse: (
        response: ServicePreset,
        _,
        { serviceForPaymentId, accountType }: TransformGetServicePresetsProps,
      ): TransformServicePreset => {
        const serviceForPayment =
          [
            ...(response.Services ?? []),
            ...(response.PreviousPayedService ? [response.PreviousPayedService] : []),
          ]?.find((el) => el.id === serviceForPaymentId) || response.CurrentService;

        const share50Percent = Object.values(response.Shares).find((i: CommonShare) => {
          return i?.model?.id === share50PercentId;
        });
        const isAvailableShare50PercentForPaidService = checkIsShareAvailableForService({
          share: share50Percent,
          accountType,
          serviceId: serviceForPayment.id,
        });

        const bonusPrepaidsShare = response.Shares[ShareNames.BonusPrepaids];
        const isBonusPrepaidsAvailable = checkIsShareAvailableForService({
          share: bonusPrepaidsShare,
          accountType,
          serviceId: serviceForPayment.id,
        });

        const isPaidServiceSuspension = response.SuspendService?.id === serviceForPayment.id;

        const transformResponse: TransformServicePreset = {
          ...response,
          serviceForPayment,
          internetLogin: null,
          isAvailableShare50PercentForPaidService,
          isPaidServiceSuspension,
          ServicesEx: response.ServicesEx || [],
          ServiceExIDs: response.ServiceExIDs || [],
          isBonusPrepaidsAvailable,
        };

        if (transformResponse.InnerAccounts && accountType === 'union') {
          let extraServices: ServiceEx[] = [];
          const ServiceExIDs: { inet: number[]; iptv: number[]; ctv: number[] } = { inet: [], iptv: [], ctv: [] };
          const entries = Object.entries(transformResponse.InnerAccounts);
          entries.forEach((item) => {
            const [key, service] = item as unknown as [UnionServicesExIdsKeys, InnerAccount];
            ServiceExIDs[key] = service.ServiceExIDs || [];
            const filteredServices = service.ServicesEx?.filter((s) => {
              return (
                (!s.IsManual || service.ServiceExIDs?.includes(s.id)) &&
                s.LimitedForServices.includes(
                  key === 'inet'
                    ? transformResponse.serviceForPayment.InetServiceID
                    : transformResponse.serviceForPayment.IptvServiceID,
                )
              );
            }).map((ex) => {
              if (service.ServiceExIDs?.includes(ex.id)) {
                return { ...ex, IsConnected: true, ExIdsKey: key };
              }
              return { ...ex, ExIdsKey: key };
            });
            extraServices = [...extraServices, ...(filteredServices?.sort((a, b) => b.Price - a.Price) || [])];
          });
          return {
            ...transformResponse,
            ServicesEx: extraServices,
            ServiceExIDs,
            internetLogin: transformResponse.InnerAccounts.inet.Access.Login,
          };
        }
        if (accountType !== 'ctv') {
          const filteredExtraServices = transformResponse.ServicesEx?.filter(
            (s) =>
              (!s.IsManual ||
                (Array.isArray(transformResponse.ServiceExIDs) && transformResponse.ServiceExIDs?.includes(s.id))) &&
              s.LimitedForServices.includes(transformResponse.serviceForPayment.id),
          )
            .map((ex) => {
              if (Array.isArray(transformResponse.ServiceExIDs) && transformResponse.ServiceExIDs?.includes(ex.id)) {
                return { ...ex, IsConnected: true };
              }
              return ex;
            })
            .sort((a, b) => b.Price - a.Price);

          return {
            ...transformResponse,
            ServicesEx: filteredExtraServices || [],
            ServiceExIDs: transformResponse.ServiceExIDs || [],
            internetLogin: accountType === 'internet' ? transformResponse.Access?.Login || null : null,
          };
        }
        return { ...transformResponse, ServicesEx: [] };
      },
      keepUnusedDataFor: 0,
      providesTags: () => ['Preset'],
    }),
    getServicePresetsAll: build.query<ServicePresetsAllResponse, void>({
      query: () => ({
        url: config.PAYMENTS.GETTING_PRESETS_FOR_ALL_SERVICES,
      }),
      keepUnusedDataFor: 0,
      providesTags: () => ['Preset'],
    }),
    getServicePresetsAllWithTransformResponse: build.query<ServicePresetsAllResponse, void>({
      query: () => ({
        url: config.PAYMENTS.GETTING_PRESETS_FOR_ALL_SERVICES,
      }),
      transformResponse: (response: ServicePresetsAllResponse): ServicePresetsAllResponse => {
        const keys = Object.keys(response) as (keyof typeof response)[];

        const transformResponse: ServicePresetsAllResponse = { ctv: [], internet: [], iptv: [], union: [] };
        keys.forEach((i) => {
          if (i === 'union') {
            const updatedArr = response[i]?.map((srv: ServicePreset) => {
              let extraServices: ServiceEx[] = [];
              const ServiceExIDs: { inet: number[]; iptv: number[]; ctv: number[] } = {
                inet: [],
                iptv: [],
                ctv: [],
              };
              if (srv.InnerAccounts) {
                const entries = Object.entries(srv.InnerAccounts);
                entries.forEach((item) => {
                  const [key, service] = item as unknown as [UnionServicesExIdsKeys, InnerAccount];

                  ServiceExIDs[key] = service.ServiceExIDs || [];

                  const filteredServices = service.ServicesEx?.filter(
                    (s) =>
                      (!s.IsManual || service.ServiceExIDs?.includes(s.id)) &&
                      s.LimitedForServices.includes(
                        key === 'inet' ? srv.CurrentService.InetServiceID : srv.CurrentService.IptvServiceID,
                      ),
                  ).map((ex) => {
                    if (service.ServiceExIDs?.includes(ex.id)) {
                      return { ...ex, IsConnected: true, ExIdsKey: key };
                    }
                    return { ...ex, ExIdsKey: key };
                  });
                  extraServices = [...extraServices, ...(srv.CurrentService.Daily ? [] : filteredServices || [])];
                });
              }
              return { ...srv, ServicesEx: extraServices.sort((a, b) => b.Price - a.Price), ServiceExIDs };
            });
            transformResponse[i] = updatedArr;
          } else if (i !== 'ctv') {
            const updatedArr = response[i]?.map((srv: ServicePreset) => {
              const filteredExtraServices = srv.ServicesEx?.filter(
                (s) =>
                  (!s.IsManual || (Array.isArray(srv.ServiceExIDs) && srv.ServiceExIDs?.includes(s.id))) &&
                  s.LimitedForServices.includes(srv.CurrentService.id),
              )
                .map((ex) => {
                  if (Array.isArray(srv.ServiceExIDs) && srv.ServiceExIDs?.includes(ex.id)) {
                    return { ...ex, IsConnected: true };
                  }
                  return ex;
                })
                .sort((a, b) => b.Price - a.Price);
              return {
                ...srv,
                ServicesEx: srv.CurrentService.Daily ? [] : filteredExtraServices || [],
                ServiceExIDs: srv.ServiceExIDs || [],
              };
            });
            transformResponse[i] = updatedArr;
          } else if (i === 'ctv') {
            const updatedArr = response[i]?.map((srv: ServicePreset) => {
              return { ...srv, ServicesEx: [], ServiceExIDs: [] };
            });
            transformResponse[i] = updatedArr;
          }
        });

        return transformResponse;
      },
      keepUnusedDataFor: 0,
      providesTags: () => ['Preset'],
    }),
    getOrdersInfo: build.query<OrdersInfoResponse[], string>({
      query: (orderCode: string) => ({
        url: config.PAYMENTS.GETTING_ORDERS_INFO(orderCode),
      }),
    }),
    getPaymentStatistics: build.query<PaymentHistory, GetPaymentHistoryProps>({
      query: ({ type, serviceType, period, accountId, subAccountId, page }) => ({
        url: config.PAYMENTS.PAYMENT_STATISTICS,
        params: {
          type,
          service_type: serviceType,
          period,
          account_id: accountId,
          subAccountId,
          page,
        },
      }),
      providesTags: () => ['History'],
    }),
    getPaymentBonuses: build.query<PaymentBonuses, { period?: string }>({
      query: ({ period }) => ({
        url: config.PAYMENTS.PAYMENT_BONUSES,
        params: { period },
      }),
      providesTags: () => ['History'],
    }),
    calculateCost: build.mutation<CalculateData, CalculateCostProps>({
      query: ({ accountId, accountType, ...params }) => ({
        url: config.PAYMENTS.CALCULATION_COST_OF_IPTV(accountId, accountType),
        method: 'POST',
        body: {
          service_id: params.serviceId,
          service_ex_ids: params.ServiceExIDs,
          date_from: params.dateFrom,
          count: params.count,
          change_current_service: params.changeCurrentService,
          is_bonus_payment: params.isBonusPayment,
          shares: params.shares,
        },
      }),
    }),
    calculateCostWithTransformResponse: build.mutation<PreparedCalculateData, CalculateCostProps>({
      query: ({ accountId, accountType, ...params }) => ({
        url: config.PAYMENTS.CALCULATION_COST_OF_IPTV(accountId, accountType),
        method: 'POST',
        body: {
          service_id: params.serviceId,
          service_ex_ids: params.ServiceExIDs,
          date_from: params.dateFrom,
          count: params.count,
          change_current_service: params.changeCurrentService,
          is_bonus_payment: params.isBonusPayment,
          shares: params.shares,
        },
      }),
      transformResponse: (response: CalculateData): PreparedCalculateData => ({
        ...response,
        view: Object.values(response.view ?? {}).find((a) => a?.length)?.[0],
      }),
    }),
    createServiceOrder: build.mutation<CreateServiceOrderResponse, CreateOrderProps>({
      query: ({ accountId, accountType, ...params }) => ({
        url: config.PAYMENTS.CREATE_SERVICE_ORDER(accountId, accountType),
        method: 'POST',
        body: {
          service_id: params.serviceId,
          service_ex_ids: params.ServiceExIDs,
          date_from: params.dateFrom,
          count: params.count,
          change_current_service: params.changeCurrentService,
          is_bonus_payment: params.isBonusPayment,
          shares: params.shares,
        },
      }),
    }),
    servicePayment: build.mutation<{ success: boolean }, CreateOrderProps & { promoBannerId?: string }>({
      query: ({ accountId, accountType, promoBannerId, ...params }) => ({
        url: config.PAYMENTS.PAYMENT(accountId, accountType),
        method: 'POST',
        body: {
          service_id: params.serviceId,
          service_ex_ids: params.ServiceExIDs,
          date_from: params.dateFrom,
          count: params.count,
          change_current_service: params.changeCurrentService,
          is_bonus_payment: params.isBonusPayment,
          shares: params.shares,
          ...(promoBannerId ? { promoBannerId } : {}),
        },
      }),
      invalidatesTags: ['Accounts', 'UserInfo', 'Preset', 'History', 'RegularPayment'],
    }),
    getLinkToPlatonForm: build.query<GetLinkToPlatonFormResponse, GetLinkToPlatonFormProps>({
      query: (props) => ({
        url: config.TOP_UP.PLATON_FORM,
        method: 'GET',
        params: {
          ...props,
        },
      }),
    }),
    googlePay: build.mutation<GooglePayPlatonResponse, GooglePayPlatonData>({
      query: ({ ...params }) => ({
        url: config.PAYMENTS.PAYMENT_GOOGLE_PAY,
        method: 'POST',
        body: {
          amount: params.amount,
          description: params.description,
          url: params.url,
          order: params.order,
          source: params.source,
          token: params.token,
          email: params.email,
        },
      }),
      invalidatesTags: ['Accounts', 'UserInfo', 'Preset', 'History', 'RegularPayment'],
    }),
  }),
});
